import { CurrencyInputField, CurrencyTypeProvider, InfoIconTooltipPopover } from "@dgs/core";
import { useFormikContext } from "formik";
import { FC, Fragment, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CostCategoryOptionResource } from "../../types/costCategory";
import { EventDetailResource, EventOffboardingFormValues } from "../../types/event";
import { EventTypeOptionResource } from "../../types/eventType";
import { EventFormColumns } from "../save/EventFormColumns";
import { Header, Heading } from "../save/EventPlannedCostStepFormFields";
import { EventOffboardingFormCard } from "./EventOffboardingFormCard";
import { EventOffboardingOverviewCard } from "./EventOffboardingOverviewCard";
import { isCostsStepValid } from "./EventOffboardingStepper";
import { shouldSkipKeyMetricsStep, useSubmitOffboardingStep } from "./eventOffboardingStepperUtils";

interface Props {
	event: EventDetailResource;
	variant: "outlined" | "elevated";
}

const isRequired = (eventType: EventTypeOptionResource | null, item: CostCategoryOptionResource): boolean =>
	eventType?.costCategoryRequirements.find((c) => c.costCategory.id === item.id)?.requirement === "required";

export const EventOffboardingCosts: FC<Props> = ({ event, variant }) => {
	const { t } = useTranslation();
	const submitStep = useSubmitOffboardingStep();
	const { values, errors, isValidating, setTouched, touched } = useFormikContext<EventOffboardingFormValues>();

	const handleSubmitClick = useCallback(async () => {
		await setTouched({
			...touched,
			costCategories: values.costCategories.map(() => ({
				plannedCost: true,
				actualCost: true,
			})),
		});

		if (!isValidating && isCostsStepValid(errors)) {
			await submitStep(event.eventType, "costs");
		}
	}, [errors, event.eventType, isValidating, setTouched, submitStep, touched, values.costCategories]);

	return (
		<>
			<EventOffboardingOverviewCard event={event} variant={variant} />
			<EventOffboardingFormCard
				title={t("Costs")}
				variant={variant}
				prevLink={shouldSkipKeyMetricsStep(event.eventType) ? "../../.." : "../key-metrics"}
				onSubmitClick={handleSubmitClick}
			>
				<CurrencyTypeProvider currencyType={event.currency || undefined}>
					{event.costCategories.map((item, index) => (
						<Fragment key={`${item.costCategory.id}`}>
							<Header>
								<Heading>{item.costCategory.name}</Heading>
								{item.costCategory.description && (
									<InfoIconTooltipPopover>{item.costCategory.description}</InfoIconTooltipPopover>
								)}
							</Header>
							{item.comment && (
								<div>
									{t("Comment")}: {item.comment}
								</div>
							)}
							<EventFormColumns>
								<CurrencyInputField
									name={`costCategories.${index}.plannedCost`}
									label={t("Planned cost")}
									disabled={true}
								/>
								<CurrencyInputField
									name={`costCategories.${index}.actualCost`}
									label={t("Actual cost")}
									required={isRequired(event.eventType, item.costCategory)}
								/>
							</EventFormColumns>
						</Fragment>
					))}
				</CurrencyTypeProvider>
			</EventOffboardingFormCard>
		</>
	);
};
